import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as emailTemplateService from '../../../services/email.template';
import CustomPagination from '../../../common/custompagination';
import Sorting from '../../../common/sorting';
import Loader from '../../../common/loader';
import Breadcrums from "../../../common/breadcrumbs";
import { SWAL_SETTINGS, EMAIL_TEMPLATE_TYPES } from '../../../../../utils/Constants';
import { formateDateWithMonth, TrimText } from '../../../../../utils/commonfunction';
import CustomRangepicker from '../../../common/rangepicker';
import { useTranslation } from "react-i18next";

const EmailTemplatesTable = () => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [statsupdate, setStatusUpdate] = useState(false);
  const [datalength, setDataLength] = useState(0);
  const [itemperpage] = useState(10);
  const [sorting, setSorting] = useState({});
  const [defaultSorting, setDefaultSorting] = useState(true);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [search, setSearch] = useState({});
  const [globalsearch, setGlobalSearch] = useState("");
  const [resetdate, setResetDate] = useState(false);

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard/1" },
    { title: t("sidebar_link_email_template"), url: "" }
  ];

  const queryParams = new URLSearchParams(location.search);
  const devParam = queryParams.get('dev');
  const isDevMode = devParam === 'true'; // Only used for Delete & Add

  useEffect(() => {
    setLoader(true);
    const formData = new FormData();
    formData.append("page", params.pgno || 1);
    formData.append("per_page", itemperpage);
    formData.append("sort", JSON.stringify(sorting));
    formData.append("search", JSON.stringify(search));
    formData.append("global_search", globalsearch);
    formData.append("type", EMAIL_TEMPLATE_TYPES.DEFAULT);

    emailTemplateService.List(formData)
      .then(response => {
        if (response?.success) {
          setList(response.data?.list || []);
          setDataLength(response.data?.total_records || 0);
          setPage(response.data?.page || 1);
        } else {
          setList([]);
          setDataLength(0);
        }
        setLoader(false);
      })
      .catch(err => {
        console.error("List error:", err);
        setLoader(false);
      });
  }, [location, statsupdate, sorting, search, globalsearch, params.pgno]);

  const viewfunction = (row) => {
    navigate(`/admin/cms/default-email-template/view/${row._id}`);
  };

  const goToEdit = (row) => {
    navigate(`/admin/cms/default-email-template/${params.pgno || 1}/edit/${row._id}`);
  };

  const Deletefunction = (data) => {
    Swal.fire({
      title: t("msg_are_you_sure"),
      text: t("you_not_be_able_to_revert_this"),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#403fad',
      cancelButtonColor: '#f1388b',
      confirmButtonText: t("btn_yes_delete"),
      cancelButtonText: t("btn_cancel")
    }).then((result) => {
      if (result.isConfirmed) {
        const ids = Array.isArray(data) ? data.map(d => d._id) : [data._id];
        const formdata = new FormData();
        formdata.append("o_id", JSON.stringify(ids));

        emailTemplateService.Delete(formdata)
          .then(response => {
            setStatusUpdate(!statsupdate);
            Swal.fire({
              icon: response.success ? 'success' : 'error',
              text: response.message || (response.success ? "Deleted!" : "Failed"),
              ...SWAL_SETTINGS
            });
          })
          .catch(err => {
            console.log("Delete error:", err);
            Swal.fire({ icon: 'error', text: "Delete failed", ...SWAL_SETTINGS });
          });
      }
    });
  };

  const handleSort = (e, column) => {
    setDefaultSorting(false);
    const order = e.target.classList.contains("assc") ? -1 : 1;
    setSorting({ order, column });
  };

  const prepareSearch = (key, value) => {
    let sr = { ...search };
    if (String(value).length > 0) {
      sr[key] = value;
    } else {
      delete sr[key];
    }
    setSearch(sr);
  };

  const resetFilter = (e) => {
    e.preventDefault();
    setGlobalSearch("");
    setSearch({});
    setSorting({});
    setDefaultSorting(true);
    setResetDate(!resetdate);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />

      <div className="animation_fade">
        <div className="card custom-card">
          <div className="card-body">
            <div className='d-flex justify-content-between align-items-center mb-4'>
              <h6 className="main-content-label">{t("sidebar_link_email_template")}</h6>
              <div className='d-flex align-items-center'>
                <div className='form-group mb-0 me-3'>
                  <div className="form-group mb-0 rangepicker_container filter_design">
                    <i className="fa fa-search calender_icon"></i>
                    <input
                      type="search"
                      className="form-control"
                      value={globalsearch}
                      placeholder="Search by title or slug..."
                      onChange={(e) => setGlobalSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="me-3">
                  <CustomRangepicker
                    GetDateRange={(e) => prepareSearch("createdAt", e)}
                    resetdate={resetdate}
                  />
                </div>

                <button
                  type="button"
                  onClick={resetFilter}
                  className="btn btn-warning me-2"
                >
                  {t("btn_reset_filter")}
                </button>

                {isDevMode && (
                  <button
                    className="btn ripple btn-main-primary signbtn"
                    onClick={() => navigate('/admin/cms/default-email-template/add?dev=true')}
                  >
                    Add New
                  </button>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                <thead>
                  <tr>
                    <th className='sr_head'>{t("list_heading_sno")}</th>
                    <th>
                      <div className='sorting_column'>
                        <span>Title (English)</span>
                        <Sorting sort={sorting} handleSort={handleSort} column="title.en" />
                      </div>
                    </th>
                    <th>
                      <div className='sorting_column'>
                        <span>Slug</span>
                      </div>
                    </th>
                    <th>
                      <div className='sorting_column'>
                        <span>Subject (English)</span>
                      </div>
                    </th>
                    <th>
                      <div className='sorting_column'>
                        <span>Created Date</span>
                        <Sorting sort={sorting} handleSort={handleSort} defaultSorting={defaultSorting} column="createdAt" />
                      </div>
                    </th>
                    <th className='action_head'>{t("list_heading_action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan="6"><Loader /></td>
                    </tr>
                  ) : list.length > 0 ? (
                    list.map((row, i) => (
                      <tr key={row._id}>
                        <td>{((page - 1) * itemperpage) + i + 1}</td>
                        <td>{row.title?.en ? TrimText(row.title.en, 30) : "N/A"}</td>
                        <td><code>{row.slug || "N/A"}</code></td>
                        <td title={row.subject?.en}>{row.subject?.en ? TrimText(row.subject.en, 40) : "N/A"}</td>
                        <td>{formateDateWithMonth(row.createdAt)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn ripple btn-main-primary signbtn btn-sm"
                              onClick={() => viewfunction(row)}
                            >
                              View
                            </button>

                            {/* Edit is ALWAYS visible */}
                            <button
                              className="btn ripple btn-success btn-sm"
                              onClick={() => goToEdit(row)}
                            >
                              Edit
                            </button>

                            {/* Delete only in dev mode */}
                            {isDevMode && (
                              <button
                                className="btn ripple btn-danger btn-sm"
                                onClick={() => Deletefunction(row)}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        {t("msg_no_records")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div id="example1_info" role="status" aria-live="polite">
              <b>{t("msg_total_records")}: {datalength}</b>
            </div>

            {datalength > 0 && (
              <CustomPagination
                datalength={datalength}
                itemperpage={itemperpage}
                currentPage={page}
                setPage={setPage}
                pageRoute={[{ name: "email-templates", path: "/admin/cms/default-email-template/list" }]}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailTemplatesTable;